import React, { useState, useRef } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import Modal from "../components/Modal";
import { fmtINR } from "../utils/formatters";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { CustomTextArea } from "../../common/FormField/FormField";
import { ErrorLabel } from "../../common/CustomSelect/CustomSelect";
import {
  SegLabel,
  TabActiveBar,
  TabButton,
  TabLabel,
  TabStrip,
} from "../WalkinBooking/Styles";
import AWSService from "../../../services/AWSService";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";

const ADMIN_CHARGE = 2500;
const MIN_REASON_LENGTH = 30;

const AdminCancelModal = ({ booking, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [feeOverride, setFeeOverride] = useState("policy");
  const [customFee, setCustomFee] = useState("");
  const [customReason, setCustomReason] = useState("");

  const [error, setError] = useState("");
  const [customError, setCustomError] = useState("");
  const [customFeeError, setCustomFeeError] = useState("");

  // Supporting document state — all new, self-contained, no shared utility modified.
  const [supportingFile, setSupportingFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  if (!booking) return null;

  const msBefore =
    new Date(booking.date + "T" + booking.startTime) - new Date();
  const hoursBefore = Math.round(msBefore / (1000 * 60 * 60));
  const daysBefore = msBefore / (1000 * 60 * 60 * 24);
  const grandTotal = booking.grand_total || 0;
  // GST is refunded — every refund is worked out on the amount including GST.

  // Mirrors the date-wise policy in backend cancelBooking. Preview only —
  // the backend computes the actual refund.
  const policy =
    daysBefore <= 7
      ? {
          rule: "0–7 days prior – No refund",
          feeText: "100%",
          fee: grandTotal,
        }
      : daysBefore <= 15
        ? {
            rule: "7–15 days prior – 50% refund",
            feeText: "50%",
            fee: grandTotal * 0.5,
          }
        : {
            rule: `More than 15 days prior – Paid amount minus ${fmtINR(ADMIN_CHARGE)} admin charges`,
            feeText: fmtINR(ADMIN_CHARGE),
            fee: Math.min(ADMIN_CHARGE, grandTotal),
          };

  const isPolicy = feeOverride === "policy";

  const appliedFeePercent =
    feeOverride === "custom"
      ? parseInt(customFee) || 0
      : parseInt(feeOverride) || 0;

  const appliedFee = isPolicy
    ? policy.fee
    : Math.ceil(grandTotal * (appliedFeePercent / 100));

  const refundAmount = grandTotal - appliedFee;

  // Counts down to the minimum; trimmed, same as the validation.
  const reasonRemaining = Math.max(
    0,
    MIN_REASON_LENGTH - reason.trim().length,
  );

  const validateForm = () => {
    let isValid = true;
    setError("");
    setCustomError("");
    setCustomFeeError("");

    if (!reason || reason.trim().length < MIN_REASON_LENGTH) {
      setError(
        `Please provide a reason (minimum ${MIN_REASON_LENGTH} characters) for audit.`,
      );
      isValid = false;
    }

    if (feeOverride === "custom") {
      const percent = parseInt(customFee);
      if (!customFee || isNaN(percent) || percent < 1 || percent > 100) {
        setCustomFeeError("Please enter a valid percentage between 1 and 100.");
        isValid = false;
      }

      if (!customReason || customReason.trim().length < 10) {
        setCustomError(
          "Please provide a reason for choosing custom percentage (min 10 characters).",
        );
        isValid = false;
      }
    }

    return isValid;
  };

  // ---- Supporting document handlers (5 MB, JPG/JPEG/PNG/PDF only) ----------
  const ALLOWED_MIME = ["image/jpeg", "image/png", "application/pdf"];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      setFileError("Only JPG, JPEG, PNG and PDF files are supported.");
      setSupportingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError("File must not exceed 5 MB.");
      setSupportingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFileError("");
    setSupportingFile(file);
  };

  const handleRemoveFile = () => {
    setSupportingFile(null);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  // ---------------------------------------------------------------------------

  // Upload happens here — only after existing form validation passes and only
  // when the user explicitly confirms. No upload occurs if the user cancels.
  const handleConfirm = async () => {
    if (!validateForm()) return;

    const base = {
      booking_id: booking?.id,
      cancelled_by: "caseManager",
      refund_mode: "offline",
      action: "cancel",
      reason: reason.trim(),
    };

    // Upload supporting document to S3 only if one has been selected.
    // Reuses existing AWSService — AWSService.js is not modified.
    if (supportingFile) {
      try {
        setLoader({ state: true, message: "Uploading supporting document..." });
        const s3Response = await AWSService.getS3URL({ key: supportingFile.name });
        if (s3Response.url) {
          base.cancelFileurl = await AWSService.uploadToS3(
            s3Response.url,
            supportingFile,
          );
        }
      } catch (_err) {
        enqueueSnackbar(
          "Failed to upload the supporting document. Please try again.",
          { variant: "error" },
        );
        setLoader({ state: false });
        return;
      } finally {
        setLoader({ state: false });
      }
    }

    // Policy: backend decides the refund from the session date.
    // Goodwill / Custom: manual override of the policy.
    onConfirm(
      isPolicy
        ? { ...base, override_policy: false }
        : {
            ...base,
            refund_percentage: appliedFeePercent,
            custom_reason: feeOverride === "custom" ? customReason.trim() : "",
            override_policy: true,
          },
    );
  };

  const hoursColor =
    daysBefore <= 7 ? "#ef4444" : daysBefore <= 15 ? "#f59e0b" : "#10b981";

  const feeOptions = [
    { id: "policy", label: "Cancel (With Policy)" },
    { id: "0", label: "0% — Goodwill" },
    { id: "custom", label: "Custom %" },
  ];

  // One option is always selected (Policy by default); the reason is kept
  // across options since every cancellation needs one.
  const handleFeeToggle = (id) => {
    if (feeOverride === id) return;
    setFeeOverride(id);
    if (id !== "custom") {
      setCustomFee("");
      setCustomReason("");
      setCustomError("");
      setCustomFeeError("");
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Cancel Booking"
      subtitle={`Ref ${booking.ref} • ${booking.room.name}`}
      width={580}
    >
      <Body>
        <AlertBox>
          <AlertContent>
            <strong>Admin Cancellation</strong>
            <p>
              This action will be logged for audit. A detailed reason is
              mandatory.
            </p>
          </AlertContent>
        </AlertBox>

        <InfoGrid>
          <InfoItem>
            <Label>Session starts in</Label>
            <Value style={{ color: hoursColor, fontWeight: 700 }}>
              {hoursBefore} hours
            </Value>
          </InfoItem>
          <InfoItem>
            <Label>Policy Default Fee</Label>
            <Value>{policy.feeText}</Value>
          </InfoItem>
        </InfoGrid>

        {/* Cancellation Fee */}
        <Section>
          <SegLabel>Cancellation Fee</SegLabel>
          <TabStrip>
            {feeOptions.map((option) => {
              const active = feeOverride === option.id;
              return (
                <TabButton
                  key={option.id}
                  type="button"
                  $active={active}
                  onClick={() => handleFeeToggle(option.id)}
                >
                  <TabLabel $active={active}>{option.label}</TabLabel>
                  <TabActiveBar $active={active} />
                </TabButton>
              );
            })}
          </TabStrip>

          {isPolicy && (
            <PolicyNote>
              Refund will be calculated as per cancellation policy:{" "}
              <strong>{policy.rule}</strong>
            </PolicyNote>
          )}

          {feeOverride === "custom" && (
            <>
              <Label style={{ marginTop: 12 }}>
                Custom percentage <span className="required-star">*</span>
              </Label>
              <CustomFeeInput
                type="number"
                value={customFee}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => {
                  setCustomFee(e.target.value);
                  if (customFeeError) setCustomFeeError("");
                }}
                placeholder="Enter percentage (1-100)"
                min="1"
                max="100"
              />
              {customFeeError && <ErrorLabel>{customFeeError}</ErrorLabel>}

              <Label style={{ marginTop: 12 }}>
                Reason for custom percentage{" "}
                <span className="required-star">*</span>
              </Label>
              <CustomTextArea
                value={customReason}
                onChange={(e) => {
                  setCustomReason(e.target.value);
                  if (customError) setCustomError("");
                }}
                placeholder="Why are you applying a custom cancellation fee?"
                rows={2}
              />
              {customError && <ErrorLabel>{customError}</ErrorLabel>}
            </>
          )}
        </Section>

        {/* Reason - mandatory for every cancellation */}
        <Section>
          <Label>
            Reason for Cancellation <span className="required-star">*</span>
          </Label>
          <CustomTextArea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError("");
            }}
            placeholder={`Explain why this booking is being cancelled (minimum ${MIN_REASON_LENGTH} characters)...`}
            rows={4}
          />
          <ReasonCounter $done={reasonRemaining === 0}>
            {reasonRemaining > 0
              ? `${reasonRemaining} more character${reasonRemaining === 1 ? "" : "s"} required`
              : `Minimum ${MIN_REASON_LENGTH} characters reached`}
          </ReasonCounter>
          {error && <ErrorLabel>{error}</ErrorLabel>}
        </Section>

        {/* Supporting Document — optional, 5 MB max, JPG/JPEG/PNG/PDF */}
        <Section>
          <SupportingDocLabel>
            Supporting Document{" "}
            <OptionalBadge>(Optional)</OptionalBadge>
          </SupportingDocLabel>
          <FileInputRow>
            <BrowseBtn
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              Browse
            </BrowseBtn>
            {supportingFile ? (
              <FileNameDisplay>
                <span title={supportingFile.name}>{supportingFile.name}</span>
                <RemoveFileBtn
                  type="button"
                  title="Remove file"
                  onClick={handleRemoveFile}
                >
                  ✕
                </RemoveFileBtn>
              </FileNameDisplay>
            ) : (
              <FileNamePlaceholder>No file selected</FileNamePlaceholder>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </FileInputRow>
          <FileHint>Accepted: JPG, JPEG, PNG, PDF — max 5 MB</FileHint>
          {fileError && <ErrorLabel>{fileError}</ErrorLabel>}
        </Section>

        <SummaryBox>
          <SummaryTitle>Payment & Refund Summary</SummaryTitle>

          <SummaryRow>
            <strong>Grand Total Paid (incl. GST)</strong>
            <strong>{fmtINR(grandTotal)}</strong>
          </SummaryRow>

          <SummaryRow>
            <span>
              Cancellation Fee (
              {isPolicy ? "As per policy" : `${appliedFeePercent}%`})
            </span>
            <RedText>{fmtINR(appliedFee)}</RedText>
          </SummaryRow>

          <Divider />

          <SummaryRow $total>
            <strong>Refund to Customer (incl. GST)</strong>
            <GreenText>{fmtINR(refundAmount)}</GreenText>
          </SummaryRow>
        </SummaryBox>

        <Actions>
          <PrimaryOutlinedCloseCTAButton
            style={{ width: "110px", padding: "12px 12px", borderRadius: 8 }}
            onClick={onClose}
          >
            Cancel
          </PrimaryOutlinedCloseCTAButton>
          <PrimaryCTAButton
            style={{ width: "auto", padding: "12px 12px", borderRadius: 8 }}
            variant="danger"
            onClick={handleConfirm}
          >
            Confirm Cancellation
          </PrimaryCTAButton>
        </Actions>
      </Body>
    </Modal>
  );
};

export default AdminCancelModal;

const Body = styled.div`
  padding: 24px;
`;

const AlertBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 14px;
  margin-bottom: 24px;
`;

const AlertContent = styled.div`
  font-size: 13.5px;
  line-height: 1.5;
  color: #991b1b;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  background: #f8fafc;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const InfoItem = styled.div``;

const Label = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11.5px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
`;

const Value = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const ReasonCounter = styled.div`
  margin-top: 6px;
  text-align: right;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: ${({ $done }) => ($done ? "#10b981" : "#64748b")};
`;

const PolicyNote = styled.div`
  background: #f0f9fa;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 10px;
  padding: 12px 14px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13.5px;
  line-height: 1.5;
  color: ${COLORS.COLOR_DARK};
`;

const CustomFeeInput = styled.input`
  margin-top: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid ${COLORS.INPUT_BORDER};
  border-radius: 10px;
  font-size: 15px;

  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: ${COLORS.BTN_GREEN};
    box-shadow: 0 0 0 4px rgba(0, 131, 140, 0.1);
  }
`;

const SummaryBox = styled.div`
  background: #f8fafc;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 28px;
`;

const SummaryTitle = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 13px;
  color: #475569;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-family: ${theme.fonts.primaryFontRegular};
  color: ${COLORS.COLOR_DARK};
  font-size: 14px;

  ${({ $total }) =>
    $total &&
    `
    font-size: 15.5px;
    font-weight: 700;
  `}
`;

const RedText = styled.span`
  color: #ef4444;
  font-weight: 600;
`;

const GreenText = styled.span`
  color: #10b981;
  font-weight: 800;
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLORS.BORDER_GREY};
  margin: 8px 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

// ---- Supporting Document field styled components --------------------------

const SupportingDocLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11.5px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
`;

const OptionalBadge = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: #94a3b8;
  text-transform: none;
  letter-spacing: 0;
`;

const FileInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const BrowseBtn = styled.button`
  padding: 8px 16px;
  border: 1.5px solid ${COLORS.INPUT_BORDER};
  border-radius: 8px;
  background: #f8fafc;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
    background: rgba(0, 131, 140, 0.05);
  }
`;

const FileNameDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: ${COLORS.COLOR_DARK};
  flex: 1;
  min-width: 0;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }
`;

const RemoveFileBtn = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 15px;
  padding: 0 4px;
  line-height: 1;
  flex-shrink: 0;
  &:hover {
    opacity: 0.7;
  }
`;

const FileNamePlaceholder = styled.span`
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #94a3b8;
  flex: 1;
`;

const FileHint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #94a3b8;
`;
